import { FileUploader, Input, Textarea, ToastMessage } from '@cokitchen/cokitchen-components';
import PageHeaderText from 'components/base/page-header-text';

import SingleBottomNav from 'components/base/single-bottom-nav';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { STORAGE_KEYS } from 'utils/constants';
import { CreateMealType } from 'utils/types';
import { getDefaultImages } from 'utils/functions';
import { DeleteIcon } from 'cokitchen-icons';
import { isEqual } from 'lodash';

const MAX_UPLOAD = 6;

const EditBaseMeal = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    active: false,
    related_meals: false
  } as CreateMealType);
  const [disabled, setDisabled] = useState(true);
  const [baseMeal, setBaseMeal] = useState({} as CreateMealType);

  const formValidity = () => {
    const { name, amount, images, description } = formData;
    if (name === '' || amount === '' || !images || description === '') {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  };
  useEffect(() => {
    formValidity();
  }, [formData]);
  const fetchBaseMealDetails = async () => {
    setLoading(true);
    try {
      const res = await apiService.get(`base/meal/${id}`, {});
      if (res.data) {
        setBaseMeal(res.data.data);
      }
    } catch {
      console.log('error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchBaseMealDetails();
  }, []);

  useEffect(() => {
    if (baseMeal.images)
      setFormData({ ...baseMeal, images: getDefaultImages(MAX_UPLOAD, baseMeal.images) });
    else setFormData(baseMeal);
  }, [baseMeal]);

  const editBaseMeal = async () => {
    setLoading(true);
    const payload: any = {
      meal_id: id
    };

    for (let i = 0; i < Object.keys(formData).length; i++) {
      const key = Object.keys(formData)[i] as keyof Omit<
        CreateMealType,
        'brand_id' | 'base_meal_id'
      >;
      if (baseMeal[key] === null && formData[key] === '') continue;
      if (baseMeal[key] === undefined) continue;
      if (!isEqual(formData[key], baseMeal[key])) {
        payload[key] = formData[key];
      }
    }
    const mealPayload = Object.keys(payload).reduce((object: any, key) => {
      if (key !== 'meal_category_id') {
        if (key === 'images') {
          object[key] = payload[key].filter((url: string) => Boolean(url));
        } else {
          object[key] = payload[key];
        }
      }
      return object;
    }, {});
    try {
      if (mealPayload.amount) {
        apiService.put(`base/price`, {
          data: { base_meals: [{ amount: mealPayload?.amount, id }] }
        });
      }
      apiService.put(`base/meal/${id}`, {
        data: { ...mealPayload, amount: undefined }
      });

      toast(<ToastMessage type='success' message={'Base meal price edited successfully'} />);
      navigate(-1);
    } catch {
      toast(<ToastMessage type='error' message={'Error while editing base meal'} />);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateImage = (url: string, index: number) => {
    const images = [...formData.images];
    images[index] = url;
    setFormData({ ...formData, images });
  };

  const handleDeleteImage = (index: number) => {
    const images = [...formData.images];
    images[index] = '';
    setFormData({ ...formData, images });
  };

  return (
    <div>
      <PageHeaderText title='Edit Base items' description='Provide details for base item' />
      {loading ? (
        'Loading'
      ) : !formData.name ? (
        <div className='w-full h-screen'>
          <div className='flex justify-center items-center'>
            <div className='mt-10'>
              <p> Error loading meal details. </p>
              <p
                onClick={() => navigate(-1)}
                className='underline cursor-pointer text-[#E60A2B] font-medium text-center'>
                Go back
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <Input
            className='my-3'
            label='Meal Name'
            placeholder='Enter meal name'
            value={formData.name}
            onChange={(e) => {
              setFormData({
                ...formData,
                name: e.target.value
              });
            }}
          />
          <Textarea
            className='my-1'
            label='Meal Description'
            placeholder='Describe the brand'
            rows={4}
            value={formData.description}
            onChange={(e) => {
              setFormData({
                ...formData,
                description: e.target.value
              });
            }}
          />
          <div className='relative my-3'>
            <Input
              label='Meal Amount'
              type='number'
              placeholder='Enter meal amount'
              pattern='[0-9]*'
              value={formData.amount}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  amount: e.target.value
                });
              }}
              onWheel={(e) => e.currentTarget.blur()}
            />
            <div className='bg-[#F2F2F3] rounded-md w-[42px] h-[27px] flex justify-center items-center absolute top-12 right-4 z-50'>
              <p className='text-xs'>NGN</p>
            </div>
          </div>
          <Input
            className='my-3'
            label='Meal Available No.'
            placeholder='Enter meal available no.'
            value={formData.available_no}
            onChange={(e) => {
              setFormData({
                ...formData,
                available_no: e.target.value
              });
            }}
          />
          <Input
            className='my-3'
            label='Minimum Age'
            placeholder='Enter minimum age'
            value={formData.minimum_age}
            type='number'
            min={0}
            max={99}
            onChange={(e) => {
              setFormData({
                ...formData,
                minimum_age: e.target.value.toString()
              });
            }}
          />
          <div className='relative'>
            <Input
              className='my-3'
              label='Calories'
              type='tel'
              placeholder='Enter calories'
              value={formData.calories}
              pattern='[0-9]*'
              onChange={(e) => {
                setFormData({
                  ...formData,
                  calories: e.target.value
                });
              }}
            />

            <div className='bg-[#F2F2F3] rounded-md w-[42px] h-[27px] flex justify-center items-center absolute top-12 right-4 z-50'>
              <p className='text-xs'>KCAL</p>
            </div>
          </div>
          <div className='relative'>
            <Input
              className='my-3'
              label='Meal preparation time'
              type='tel'
              placeholder='Enter duration'
              pattern='[0-9]*'
              value={formData.preparation_time}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  preparation_time: e.target.value
                });
              }}
            />
            <div className='bg-[#F2F2F3] rounded-md w-[42px] h-[27px] flex justify-center items-center absolute top-12 right-4 z-50'>
              <p className='text-xs'>MINS</p>
            </div>
          </div>

          <div className='my-4 bg-[#F9F6F6] grid grid-cols-2 gap-3 rounded-md border-[0.5px] border-[#EEECEC] px-[20px] py-3'>
            <Input
              type='checkbox'
              label='Sold out? '
              checked={!formData.active}
              inputClass='font-medium text-xl'
              onChange={(e) => {
                setFormData({ ...formData, active: !e.target.checked });
              }}
            />
            <Input
              type='checkbox'
              label='Update related meals? '
              checked={formData.related_meals}
              inputClass='font-medium text-xl'
              onChange={(e) => {
                setFormData({ ...formData, related_meals: e.target.checked });
              }}
            />
          </div>
          <div className='border border-[#EAEBEB] rounded-xl mt-6 '>
            <div className='px-6 py-[18px]'>
              <p className=' text-[#1A1A1A] text-sm  font-medium '>Base Meal Image (optional)</p>
              <p className='text-[#595959] text-xs'>Upload up to 6 images</p>
            </div>
            <hr className='text-[#EAEBEB]' />
            <div className='p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4'>
              {formData?.images?.map((url: string, index: number) => (
                <div key={`${url}${index}`} className='relative group '>
                  <FileUploader
                    token={token}
                    fileUrls={url ? [url] : []}
                    setFileUrls={(urls) => handleUpdateImage(urls[0], index)}
                    className='h-[160px]'
                  />

                  {url ? (
                    <div
                      onClick={() => handleDeleteImage(index)}
                      className='absolute top-[14px] right-5 cursor-pointer opacity-0 group-hover:opacity-100 '>
                      <DeleteIcon className='text-[#fff]' />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <SingleBottomNav btnDisabled={disabled} loading={loading} onConfirm={editBaseMeal} />
    </div>
  );
};

export default EditBaseMeal;
